import { faExternalLink } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';

import { getAccount } from 'common/api/users/service';
import { getPhonesByUser } from 'common/api/users/twofactor/phone';
import { useRootSelector } from 'common/features/featuresReducer';
import { useApi } from 'common/modules/async-actions/useApi';
import { useAlert } from 'modules/alerts';
import { colors } from 'styles/settings';
import { createStyles } from 'utils/createStyle';

const styles = createStyles({
    title: 'text-xs text-grey-1 font-bold',
    title2: 'flex items-center text-xs',
    defaultBtn: [
        'text-brand border border-brand',
        {
            fontSize: '10px',
            borderRadius: '3px',
            padding: '0 10px',
            lineHeight: '15px',
            "[data-selected='true']": {
                backgroundColor: colors.brandDark,
                color: 'white',
            },
        },
    ],
    subtitle: ['text-xs text-grey-3'],
});

const DEFAULT_ERR_MSG =
    "It wasn't possible to retrieve your security information at this moment. Please try again in a few minutes.";

export const SecuritySettingsContent = () => {
    const userId = useRootSelector((r) => r.store.account.userId);
    const alert = useAlert();

    const [doGetPhonesByUser, phonesAsyncState, phones] = useApi(getPhonesByUser);
    const [doGetAccount, accountAsyncState, account] = useApi(getAccount);

    useEffect(() => {
        if (userId) {
            (async () => {
                await doGetPhonesByUser(userId);
                await doGetAccount();
            })();
        }
    }, [userId, doGetPhonesByUser, doGetAccount]);

    useEffect(() => {
        if (phonesAsyncState.error || accountAsyncState.error) {
            alert({
                message: DEFAULT_ERR_MSG,
                title: 'Error',
            });
            return;
        }
    }, [alert, phonesAsyncState.error, accountAsyncState.error]);

    const formattedLastPasswordUpdate = useMemo(() => {
        if (!account?.data?.lastPasswordUpdate) {
            return 'Password has never been reset';
        }
        return `Last updated ${new Date(account.data.lastPasswordUpdate).toLocaleDateString(
            'en-US'
        )}`;
    }, [account]);

    const changePasswordUrl = process.env.REACT_APP_PASSWORD_CHANGE_URL;
    const manage2FA = `${process.env.REACT_APP_MANAGE_2FA_URL}?returnUrl=${window.location.href}`;

    return (
        <div>
            <div>
                <div className={styles.title2}>
                    <span className={styles.title}>Password</span>
                    <a className="ml-auto font-bold" href={changePasswordUrl}>
                        Change&nbsp;
                        <span className="inline-block">
                            <FontAwesomeIcon icon={faExternalLink} />
                        </span>
                    </a>
                </div>
                <div className="text-lg text-brand-dark">
                    {!accountAsyncState.loading && account?.data && (
                        <>
                            <div>(Password Hidden)</div>
                            <div className={styles.subtitle}>{formattedLastPasswordUpdate}</div>
                        </>
                    )}
                    {accountAsyncState.loading && <Skeleton />}
                </div>
            </div>
            <div className="mt-14">
                <div className={styles.title2}>
                    <span className={styles.title}>2-Factor Recovery</span>
                    <a className="ml-auto font-bold" href={manage2FA}>
                        Edit&nbsp;
                        <span className="inline-block">
                            <FontAwesomeIcon icon={faExternalLink} />
                        </span>
                    </a>
                </div>
                <div className="text-lg text-brand-dark">
                    {!phonesAsyncState.loading && phones?.data && (
                        <SetDefaultItemList
                            items={phones.data.map(({ id, maskedPhoneNumber, enrollmentDate }) => ({
                                id,
                                phone: maskedPhoneNumber,
                                registrationDate: enrollmentDate ? new Date(enrollmentDate) : null,
                            }))}
                        />
                    )}
                    {phonesAsyncState.loading && <Skeleton />}
                </div>
            </div>
        </div>
    );
};

interface DefaultPhoneItem {
    id: string;
    phone: string;
    registrationDate: Date | null;
}

const formatRegisteredDate = (d: Date) => {
    return `Registered ${d.toLocaleDateString()}`;
};

const SetDefaultItemList = ({ items }: { items: DefaultPhoneItem[] }) => {
    return (
        <ul>
            {items.map((item) => (
                <li key={item.id} className="mb-5">
                    <div className="flex items-center">
                        <div className="text-brand-dark">
                            <div>{item.phone}</div>
                            {item.registrationDate && (
                                <div className={styles.subtitle}>
                                    {formatRegisteredDate(item.registrationDate)}
                                </div>
                            )}
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    );
};
